/**
 * Helper function that propogates the style color based on the passed in prop
 *
 * param {String} option - color name from Contentful
 * returns {String} - style coloring
 */
export const textColorMap = (option: string) => {
  switch (option) {
    case 'hbgBlue':
      return 'text-hbg-blue';
    case 'white':
      return 'text-white';
    case 'black':
      return 'text-black-standard';
    case 'claytonBlue':
      return 'text-clayton-blue';
    case 'grey-1':
      return 'text-grey-1';
    case 'grey-2':
      return 'text-grey-2';
    case 'grey-3':
      return 'text-grey-3';
    case 'grey-4':
      return 'text-grey-4';
    case 'grey-5':
      return 'text-grey-5';
    case 'grey-6':
      return 'text-grey-6';
    default:
      return 'text-hbg-blue';
  }
};

/**
 * Helper function that propogates the font size based on the passed in prop
 *
 * param {String} option - font size from Contentful
 * returns {String} - font size
 */
export const fontSizeMap = (option: string) => {
  switch (option) {
    case 'small':
    case 'Small':
      return 'text-[16px] md:text-[18px] lg:text-[22px]';
    case 'medium':
    case 'Medium':
      return 'text-[22px] md:text-[26px] lg:text-[35px]';
    case 'large':
    case 'Large':
      return 'text-[34px] md:text-[2.75rem] lg:text-[3.75rem]';
    default:
      return 'text-[22px] md:text-[26px] lg:text-[35px]';
  }
};

/**
 * Helper function that propogates the style color based on the passed in prop
 *
 * param {String} option - color name from Contentful
 * returns {String} - style coloring
 */
export const bgColorMap = (option) => {
  switch (option) {
    case 'hbgBlue':
    case '#1E486F':
      return 'bg-hbg-blue';
    case 'white':
    case '#FFFFFF':
      return 'bg-white';
    case 'black':
      return 'bg-black-standard';
    case '#000000':
      return 'bg-[#000000]';
    case 'claytonBlue':
      return 'bg-clayton-blue';
    case 'grey-1':
      return 'bg-grey-1';
    case 'grey-2':
      return 'bg-grey-2';
    case 'grey-3':
      return 'bg-grey-3';
    case 'grey-4':
      return 'bg-grey-4';
    case 'grey-5':
      return 'bg-grey-5';
    case 'grey-6':
      return 'bg-grey-6';
    default:
      return 'bg-transparent';
  }
};

/**
 * Helper function that propogates the top padding based on the passed in prop
 *
 * param {String} size - padding top from Contentful
 * returns {String} - padding top dynamic style
 */
export const paddingTopSizeMap = (size: string) => {
  switch (size) {
    case '0':
      return 'pt-0';
    case '5':
      return 'pt-[5px]';
    case '10':
      return 'pt-2.5';
    case '15':
      return 'pt-[15px]';
    case '20':
      return 'pt-5';
    case '25':
      return 'pt-[25px]';
    case '30':
      return 'pt-[30px]';
    case '35':
      return 'pt-[35px]';
    case '40':
      return 'pt-10';
    case '45':
      return 'pt-[45px]';
    case '50':
      return 'pt-[50px]';
    default:
      return 'pt-[30px]';
  }
};

/**
 * Helper function that propogates bottom padding based on the passed in prop
 *
 * param {String} size - padding bottom size from Contentful
 * returns {String} - padding bottom dynamic style
 */
export const paddingBotSizeMap = (size: string) => {
  switch (size) {
    case '0':
      return 'pb-0';
    case '5':
      return 'pb-[5px]';
    case '10':
      return 'pb-2.5';
    case '15':
      return 'pb-[15px]';
    case '20':
      return 'pb-5';
    case '25':
      return 'pb-[25px]';
    case '30':
      return 'pb-[30px]';
    case '35':
      return 'pb-[35px]';
    case '40':
      return 'pb-10';
    case '45':
      return 'pb-[45px]';
    case '50':
      return 'pb-[50px]';
    default:
      return 'pb-[30px]';
  }
};

/**
 * Helper function that propogates hero wave height based on the passed in prop
 *
 * param {String} size - hero wave from Contentful
 * returns {String} - hero wave dynamic style
 */
export const heroWaveSizeMap = (size: number) => {
  switch (size) {
    case 1:
      return 'h-4 smDesktop:h-6';
    case 2:
      return 'h-8 smDesktop:h-14';
    case 3:
      return 'h-12 smDesktop:h-20';
    case 4:
      return 'h-16 smDesktop:h-28';
    case 5:
      return 'h-20 smDesktop:h-[8.5rem]';
    case 6:
      return 'h-24 smDesktop:h-40';
    case 7:
      return 'h-28 smDesktop:h-48';
    case 8:
      return 'h-32 smDesktop:h-[13.5]';
    case 9:
      return 'h-36 smDesktop:h-[15.5rem]';
    default:
      return 'h-20 smDesktop:h-[8.5rem]';
  }
};

/**
 * Helper function that propogates video max height based on the passed in prop
 *
 * param {String} size - video hero from Contentful
 * returns {String} - video hero dynamic style
 */
export const videoHeroSizeMap = (size: string) => {
  switch (size) {
    case 'none':
      return 'max-h-0';
    case '50vh':
      return 'max-h-[50vh]';
    case '55vh':
      return 'max-h-[55vh]';
    case '60vh':
      return 'max-h-[60vh]';
    case '65vh':
      return 'max-h-[65vh]';
    case '70vh':
      return 'max-h-[70vh]';
    case '75vh':
      return 'max-h-[75vh]';
    case '80vh':
      return 'max-h-[80vh]';
    case '85vh':
      return 'max-h-[85vh]';
    case '90vh':
      return 'max-h-[90vh]';
    case '95vh':
      return 'max-h-[95vh]';
    default:
      return 'max-h-[70vh]';
  }
};
